import { $, authHash, isAuthHash } from "./lib/dom.js";
import { defaultConsoleRoute, navItemByRoute, setExclusiveGroupOpen } from "./nav.js";
import { onRouteActivate } from "./panels/index.js";
import {
  emitRoute,
  markNavActive,
  moduleTitleFromNav,
  revealExtShell,
  setExtNotice,
  setPageTitle,
} from "./panels/ext-shell.js";
import { DIST_MD_EMBED, showDistMdPanel } from "./panels/dist-md-embed.js";

let dramaLoader = null;

export { emitRoute };

/** NAV 项 `embedPanel` 标记 → 承载函数。**跨域嵌入面板走这条**，不走 `#ext=`。
 *
 * 为什么不能塞进 `showExtPanel`：那条路把地址交给 `normalizeExtHref`，
 * 只取 `pathname + search` ⇒ 跨域 host 被削掉。见 `panels/dist-md-embed.js` 文件头。
 * 未知标记**原地降级**（不认就不动），别让它掉进 `setRoute` 末尾的
 * 「未知路由 → 跳第一项」分支 —— 那样会静默把用户弹去别的页。
 */
const EMBED_PANELS = { [DIST_MD_EMBED]: showDistMdPanel };

function openGroupOfRoute(route) {
  const link = document.querySelector(`#side-nav [data-route="${route}"]`);
  const group = link && link.closest(".mcp-tree-group");
  if (group) setExclusiveGroupOpen(group);
}

function normalizeExtHref(href) {
  if (!href) return "";
  try {
    const u = new URL(href, window.location.origin);
    return u.pathname + u.search;
  } catch {
    return String(href);
  }
}

/** iframe 实际地址（同源）；子页内链跳转后 src 属性不会变，必须读 live location */
function extFrameLivePath(frame) {
  if (!frame) return "";
  try {
    const loc = frame.contentWindow && frame.contentWindow.location;
    if (!loc || !loc.href || String(loc.href).startsWith("about:")) return "";
    return normalizeExtHref(loc.pathname + loc.search);
  } catch {
    return "";
  }
}

function forceExtFrameSrc(frame, href) {
  if (!frame || !href) return;
  const target = normalizeExtHref(href);
  const abs = (() => {
    try {
      return new URL(href, window.location.origin).href;
    } catch {
      return href;
    }
  })();
  try {
    const loc = frame.contentWindow && frame.contentWindow.location;
    if (loc && loc.href && !String(loc.href).startsWith("about:")) {
      loc.assign(abs);
      frame.setAttribute("src", href);
      return;
    }
  } catch {
    /* fall through */
  }
  const attr = normalizeExtHref(frame.getAttribute("src") || "");
  if (attr === target) {
    frame.src = "about:blank";
    setTimeout(() => {
      frame.src = href;
    }, 0);
  } else {
    frame.src = href;
  }
}

function showExtPanel(href) {
  if (!href) return;
  const target = normalizeExtHref(href);
  revealExtShell();
  // 这个面板与「尺码组」跨域嵌入共用 `#panel-ext`：先把上一条提示摘掉
  setExtNotice("");
  const frame = $("ext-frame");
  if (frame) {
    const live = extFrameLivePath(frame);
    if (live && live === target) {
      try {
        frame.contentWindow.location.reload();
      } catch {
        forceExtFrameSrc(frame, href);
      }
    } else {
      // live 已跳到别页（如指南→测试台）或首次加载：强制回到目标，勿仅 reload（reload 会停在子页）
      forceExtFrameSrc(frame, href);
    }
  }
  let active = null;
  document.querySelectorAll("#side-nav a").forEach((a) => {
    if (a.dataset.extHref && normalizeExtHref(a.dataset.extHref) === target) active = a;
  });
  markNavActive(active);
  setPageTitle(moduleTitleFromNav(active) || "商家控制台");
  emitRoute("__ext");
}

async function ensureDramaModule() {
  if (!dramaLoader) {
    dramaLoader = import("./drama/index.js");
  }
  return dramaLoader;
}

const GUIDE_START = "/static/guide/start.html?v=20260918-guide-v1";

export function setRoute(name) {
  const route = name || "overview";
  if (route.indexOf("ext=") === 0) {
    showExtPanel(decodeURIComponent(route.slice(4)));
    return;
  }
  // 旧「文档中心」#docs → 开始使用静态页
  if (route === "docs") {
    const next = "ext=" + encodeURIComponent(GUIDE_START);
    history.replaceState(null, "", "#" + next);
    showExtPanel(GUIDE_START);
    return;
  }
  // 承载标记（`embedPanel`）在 **NAV 数据**里，不在 DOM 上（`a[data-route]` 只带 route），
  // 所以回查一次数据。必须挡在下面「未知路由 → 跳第一项」之前。
  const navItem = navItemByRoute(route);
  if (navItem && navItem.embedPanel) {
    const open = EMBED_PANELS[navItem.embedPanel];
    if (open) open();
    return; // 不认的标记也**原地不动**，别把用户弹去别的页
  }
  const col = document.querySelector(".mc-maincol");
  if (col) col.classList.remove("mc-maincol--ext");
  if (!document.querySelector(`#side-nav [data-route="${route}"]`) && route !== "brand") {
    const first = document.querySelector("#side-nav [data-route]");
    if (first && first.dataset.route && first.dataset.route !== route) {
      history.replaceState(null, "", "#" + first.dataset.route);
      setRoute(first.dataset.route);
      return;
    }
  }
  const main = document.querySelector(".mc-main");
  if (main) main.classList.remove("mc-main--ext");
  document.querySelectorAll(".mcp-panel").forEach((p) => {
    p.classList.toggle("hidden", p.dataset.route !== route);
  });
  if (main) {
    main.classList.toggle("mc-main--ext", route === "actors");
  }
  document.querySelectorAll("#side-nav a").forEach((a) => {
    a.classList.toggle("active", a.dataset.route === route);
  });
  document.querySelectorAll("#overview-drama-links a").forEach((a) => {
    a.classList.toggle("active", a.dataset.route === route);
  });
  openGroupOfRoute(route);
  const active = document.querySelector(`#side-nav [data-route="${route}"]`);
  setPageTitle(moduleTitleFromNav(active) || "商家控制台");
  onRouteActivate(route);
  emitRoute(route);
  if (route === "drama" || route === "drama-monitor") {
    ensureDramaModule().then((mod) => {
      if (mod && mod.onActivate) mod.onActivate();
    });
  }
}

export function routeFromHash() {
  const h = authHash() || "";
  if (isAuthHash(h)) return defaultConsoleRoute();
  return h || defaultConsoleRoute();
}

export function initRouter(authGate) {
  function onHashChange() {
    const h = authHash();
    if ($("auth-screen") && !$("auth-screen").classList.contains("hidden")) {
      if (h === "register") authGate.showRegisterView(false);
      else if (h === "login" || !h) authGate.showLoginView(false);
      return;
    }
    if ($("console-screen") && !$("console-screen").classList.contains("hidden")) {
      setRoute(routeFromHash());
    }
  }
  window.addEventListener("hashchange", onHashChange);

  // 同 hash 再点侧栏：浏览器不触发 hashchange；外链 iframe 内跳走后须强制重入
  document.addEventListener("click", (e) => {
    const a = e.target && e.target.closest && e.target.closest("#side-nav a[href^='#']");
    if (!a) return;
    if ($("console-screen") && $("console-screen").classList.contains("hidden")) return;
    const raw = a.getAttribute("href") || "";
    if (!raw.startsWith("#")) return;
    const next = raw.slice(1);
    const cur = (window.location.hash || "").replace(/^#/, "");
    if (!next || next !== cur) return;
    e.preventDefault();
    setRoute(next);
  });

  return { onHashChange, setRoute, routeFromHash };
}

/* 预加载短剧模块，避免进入 #drama 后抢点「智能生成」时事件尚未绑定 */
import("./drama/index.js");
